// Lightweight bottom-right toast. Non-modal (no focus trap, no
// backdrop) and sticky by default — the tutorial offer should wait for
// the user rather than vanish. Sits below the ≈100 z-index dialogs
// (login modal, command palette, name modal) at z-index 90 so a toast
// never overlaps an open dialog. Region is created lazily on first use,
// mirroring login-modal.ts.

const REGION_ID = 'toast-region';

export interface ToastAction {
  label: string;
  /// Render with the accent fill (`.btn-primary`). At most one per toast.
  primary?: boolean;
  /// Invoked on click. The toast auto-dismisses afterwards.
  onClick: () => void;
}

export interface ToastOptions {
  message: string;
  actions?: ToastAction[];
  /// Auto-dismiss after this many ms. Omitted = sticky (caller dismisses
  /// via the returned handle or an action click).
  durationMs?: number;
}

export interface ToastHandle {
  dismiss(): void;
}

function region(): HTMLElement {
  let el = document.getElementById(REGION_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = REGION_ID;
    document.body.appendChild(el);
  }
  return el;
}

/// Show a toast. Returns a handle whose `dismiss()` removes it (safe to
/// call more than once).
export function showToast(opts: ToastOptions): ToastHandle {
  const host = region();

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');

  const msg = document.createElement('div');
  msg.className = 'toast-message';
  msg.textContent = opts.message;
  toast.appendChild(msg);

  let timer: ReturnType<typeof setTimeout> | undefined;
  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    if (timer !== undefined) clearTimeout(timer);
    toast.remove();
  };

  if (opts.actions && opts.actions.length > 0) {
    const row = document.createElement('div');
    row.className = 'toast-actions';
    for (const action of opts.actions) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = action.primary ? 'btn btn-primary' : 'btn';
      btn.textContent = action.label;
      btn.addEventListener('click', () => {
        action.onClick();
        dismiss();
      });
      row.appendChild(btn);
    }
    toast.appendChild(row);
  }

  host.appendChild(toast);

  if (opts.durationMs !== undefined) {
    timer = setTimeout(dismiss, opts.durationMs);
  }

  return { dismiss };
}
